import {AfterViewInit, Component, Input, OnInit, Renderer2, ViewChild} from '@angular/core';
import {QuestionComponent} from '../question.component';
import {QuestionBase} from '../../domain/question.base';
import {FormGroup} from '@angular/forms';
import {ScrollService} from '../scroll.service';

@Component({
  selector: `crds-question-button-group`,
  template: `
    <div class="crds-question-button-group" *ngIf="optionsPopulated && show">
      <label *ngIf="question.label" class="form-label"
             [attr.for]="question.key">{{question.label + (question.required ? ' *' : '')}}
          <crds-form-error *ngIf="isInvalid" [question]="question"
                         [message]="question.errorMessage"></crds-form-error>
        <div [formGroup]="form">
          <div class="btn-group btn-group-toggle"
               ngbRadioGroup
               [name]="question.key"
               [formControlName]="question.key">
            <label *ngFor="let opt of question['options'];" ngbButtonLabel [class]="'btn button-group-spaced ' + getButtonClass(opt)">
              <input ngbButton type="radio"
                     (click)="updateDependantFields(opt)"
                     [value]="opt.value"
                     #buttonGroupInput>
              {{opt.key}}
            </label>
          </div>
        </div>
      </label>

      <!-- QUESTION HINT -->
      <crds-question-hint [question]="question"></crds-question-hint>

      <p *ngIf="alert">
        <ngb-alert [dismissible]="false" type="secondary">
          <i class="material-icons">info</i> {{question.alert[form.get(question.key).value]}}
        </ngb-alert>
      </p>
    </div>
  `,
})
export class ButtonGroupQuestionComponent extends QuestionComponent implements OnInit, AfterViewInit {
  @Input() form: FormGroup;
  @Input() question: QuestionBase<any>;
  @ViewChild('buttonGroupInput') input;
  type: string;
  valueWhenDisabled;

  constructor(private scrollService: ScrollService) {
    super();
  }

  /**
   * Initialise the default form values.
   */
  ngOnInit() {
    this.question['options'] = this.question['options'] || [];
    this.scrollService.scrollObservable
      .subscribe((documentId) => {
        if (documentId === this.question.key) {
          this.input.nativeElement.scrollIntoView();
        }
      });
  }

  ngAfterViewInit() {
    this.form.get(this.question.key).valueChanges
      .subscribe(() => {
        this.updateDependantFields(this.formValue);
      });
  }

  get optionsPopulated(): boolean {
    return this.question.options && this.question.options.length > 0;
  }

  get isInvalid() {
    return this.form.get(this.question.key).touched && this.form.get(this.question.key).invalid;
  }

  get show() {
    const showIfQuestionPopulated = this.question.showIf ?
      this.showIf :
      true;
    return (this.question.showIfPopulated !== true || (this.value)) &&
      showIfQuestionPopulated && this.question.show !== false;
  }

  get alert() {
    return this.question.alert && this.question.alert[this.form.get(this.question.key).value];
  }

  get formValue() {
    return this.form.get(this.question.key).value;
  }

  /**
   * Enable or disable any fields that need updating based on the value selected.
   * We temporarily store any values when disabling.
   * We restore any previously set values when enabling.
   * If no previously set value, then mark field as untouched to avoid form error on changes.
   * @param value
   */
  updateDependantFields(value: any) {
    const opt = this.question.options.filter(option => option.value === value)[0];
    if (typeof opt === 'undefined') {
      return;
    }
    let scrollToField = null;
    if (opt.enables) {
      opt.enables.forEach(enable => {
        this.form.get(enable).enable();
        if (this.valueWhenDisabled) {
          this.form.get(enable).patchValue(this.valueWhenDisabled);
        } else {
          this.form.get(enable).markAsUntouched();
        }
        if (!scrollToField && opt.scrollTo) {
          scrollToField = enable;
        }
      });
    }
    if (opt.disables) {
      opt.disables.forEach(disable => {
        this.valueWhenDisabled = this.form.get(disable).value;
        this.form.get(disable).reset();
        this.form.get(disable).disable();
      });
    }
    if (scrollToField !== null) {
      this.scrollService.next(scrollToField);
    }
  }

  getButtonClass(opt) {
    return this.form.get(this.question.key).value === opt.value ? 'btn-primary' : 'btn-default';
  }
}
